import { type ComponentProps } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { Spinner } from '@/components/Spinner/Spinner';
import { cn } from '@/util/utils';
import type { AsChildProps } from '@/typings/common';

type ButtonProps = ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> &
    AsChildProps & {
        /**
         * The design's Loading state: a spinner before the label, and the button cannot be
         * pressed again. The label stays, so the button keeps its width and says what is
         * happening ("Logging in").
         */
        loading?: boolean;
    };

/**
 * ui-button, READ from the design system's Button (123:4149): 36 high, px 16, gap 8,
 * rounded-md, the label in the condensed uppercase label-md style. Hover and active are one
 * uniform dark overlay over the resting colour (--state-hover / --state-active), so every
 * variant darkens by the same amount.
 */
function Button({
    className,
    variant,
    size,
    asChild = false,
    loading = false,
    disabled,
    children,
    ref,
    ...props
}: ButtonProps) {
    const Component = asChild ? Slot : 'button';
    return (
        <Component
            data-slot="button"
            data-loading={loading || undefined}
            className={cn(buttonVariants({ variant, size, className }))}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
            ref={ref}
            {...props}
        >
            {asChild ? (
                children
            ) : (
                <>
                    {loading ? (
                        <Spinner aria-hidden aria-label={undefined} role={undefined} />
                    ) : null}
                    {children}
                </>
            )}
        </Component>
    );
}

const buttonVariants = cva(
    [
        'pointer-coarse:min-h-11 pointer-coarse:min-w-11 text-label-md relative isolate inline-flex shrink-0 items-center justify-center gap-2 overflow-hidden rounded-md border border-transparent whitespace-nowrap select-none',
        'transition-[color,box-shadow] duration-(--duration-fast)',
        // interaction overlay
        'after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:bg-black after:opacity-0 after:transition-opacity after:duration-(--duration-fast)',
        'hover:not-disabled:after:opacity-(--state-hover) active:not-disabled:after:opacity-(--state-active)',
        // focus, per base.css: a 2px ring outside the control
        'outline-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-ring',
        'disabled:cursor-not-allowed disabled:opacity-50 data-loading:cursor-progress',
        // The glyph is 15px, 14 in the sm size (primitives.js).
        '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=size-])]:size-[15px]',
    ],
    {
        variants: {
            variant: {
                primary: 'bg-primary text-primary-foreground shadow-xs',
                outline: 'border-border bg-card text-foreground shadow-xs',
                ghost: 'bg-transparent text-foreground',
                destructive: 'bg-destructive text-primary-foreground shadow-xs',
                'destructive-outline':
                    'border-destructive bg-background text-destructive shadow-xs',
                // Variant=Link (3012:477): a label in base/link and nothing else, no fill, stroke,
                // shadow or side padding, underlined on hover, in the 10px label.
                link: 'text-link text-label-sm bg-transparent px-0 shadow-none after:hidden hover:underline',
            },
            size: {
                default: 'h-9 px-4',
                sm: 'h-8 px-3 [&_svg:not([class*=size-])]:size-3.5',
                icon: 'size-9 px-0',
                // Ours, for icon buttons the prototype has no twin for.
                'icon-sm': 'size-8 px-0',
                // The prototype's dense-row extensions (ui-button--icon-26 and --icon-24).
                'icon-26': 'size-[26px] px-0',
                'icon-24': 'size-6 px-0',
            },
        },
        // After the size, so the link keeps no side padding whatever its size.
        compoundVariants: [{ variant: 'link', className: 'px-0' }],
        defaultVariants: {
            variant: 'primary',
            size: 'default',
        },
    },
);

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants, type ButtonProps };
